/**
 * A practice on the phone until the server has it (ruled 1 November: cages can
 * have no signal). It is written here the moment SwingReplay is left, before
 * anything is tapped, then sent; a send that fails is tried again when the app
 * opens or the signal returns.
 *
 * The id was made on the phone, so a repeated send cannot make two practices.
 * If the phone is wiped before it reconnects, the practice is lost.
 *
 * No imports: the store and the calls are handed in, so the tests run this file
 * itself.
 */

export interface PendingPractice {
  id: string;
  athleteClientId: string;
  practiceDate: string;
  firstSwingAt: string;
  lastSwingAt: string;
  swingCount: number;
  savedAt: number;
  /** The server has the swings and time. */
  sent: boolean;
  /** Done was tapped. */
  finished: boolean;
  /** Type and notes to send; null when Done was tapped with neither. */
  edited: { types: string[]; notes: string } | null;
}

export interface OutboxStore {
  read(): PendingPractice[];
  write(list: PendingPractice[]): void;
}

export interface OutboxCalls {
  save(practice: PendingPractice): Promise<unknown>;
  update(id: string, edited: { types: string[]; notes: string }): Promise<unknown>;
}

/** A Done screen left open and abandoned stops being held after this. */
const HELD_MS = 24 * 60 * 60 * 1000;

export function practiceOutbox(store: OutboxStore, calls: OutboxCalls, now: () => number = Date.now) {
  let sending = false;
  let again = false;

  const change = (id: string, edit: (practice: PendingPractice) => PendingPractice) =>
    store.write(store.read().map((practice) => (practice.id === id ? edit(practice) : practice)));
  const remove = (id: string) => store.write(store.read().filter((practice) => practice.id !== id));

  async function sendOne(id: string): Promise<void> {
    const first = store.read().find((practice) => practice.id === id);
    if (!first) return;
    if (!first.sent) {
      await calls.save(first);
      change(id, (practice) => ({ ...practice, sent: true }));
    }
    const latest = store.read().find((practice) => practice.id === id);
    if (!latest) return;
    if (latest.finished) {
      if (latest.edited) await calls.update(id, latest.edited);
      remove(id);
    } else if (now() - latest.savedAt > HELD_MS) {
      remove(id);
    }
  }

  async function flush(): Promise<void> {
    if (sending) {
      again = true;
      return;
    }
    sending = true;
    try {
      for (const { id } of store.read()) {
        try {
          await sendOne(id);
        } catch (cause) {
          // The API can never take this one; anything else may yet go through.
          if ((cause as { status?: number }).status === 400) remove(id);
          console.error('Sending a practice failed; tried again later:', cause);
        }
      }
    } finally {
      sending = false;
    }
    if (again) {
      again = false;
      await flush();
    }
  }

  return {
    flush,
    find: (id: string) => store.read().find((practice) => practice.id === id) ?? null,
    add(practice: Omit<PendingPractice, 'savedAt' | 'sent' | 'finished' | 'edited'>) {
      store.write([...store.read(), { ...practice, savedAt: now(), sent: false, finished: false, edited: null }]);
      void flush();
    },
    finish(id: string, types: string[], notes: string) {
      const edited = types.length || notes.trim() ? { types, notes: notes.trim() } : null;
      change(id, (practice) => ({ ...practice, finished: true, edited }));
      void flush();
    },
  };
}
